import { getNetworkTime, RemoteStreamType } from 'trtc-js-sdk-core';
import { AudioDecodeProcessor, AudioEncodeProcessor } from 'trtc-js-sdk-core/src/manager/audio-manager';
import { VideoDecodeProcessor, VideoEncodeProcessor } from 'trtc-js-sdk-core/src/manager/video-manager';
import { getStartCodeLengthAt, NALU, parseNALUs } from 'trtc-js-sdk-core/src/utils/nalu';
import { encodeSEINalu, getNaluCount, hasSEI, isEmptyFrame, SEI_MESSAGE } from 'trtc-js-sdk-core/src/utils/codec-pipeline/sei-utils';
import { concatArrayBuffers, getLast16Bits } from 'trtc-js-sdk-core/src/utils/utils';
function createSenderTransfrom({ seiMessageList, isAudio, getNtpTime, isMain } :{ seiMessageList?: SEI_MESSAGE[], isAudio: boolean, isMain: boolean, getNtpTime: () => number }) {
  return new TransformStream({
    transform(frame: RTCEncodedVideoFrame | RTCEncodedAudioFrame, controller) {
      let tempFrame = frame;
      if (isAudio) {
        // @ts-ignore
        (audioEncodePipeline as AudioEncodeProcessor[]).forEach(processor => {
          tempFrame = processor({ frame: tempFrame, ntp: getNtpTime(), onDump: () => {
            self.postMessage({
              type: 'dump',
              isAudio,
              data: tempFrame.data,
              userId: ''
            });
          } });
        });
      } else {
        // @ts-ignore
        (videoEncodePipeline as VideoEncodeProcessor[]).forEach(processor => {
          tempFrame = processor({ frame: tempFrame as RTCEncodedVideoFrame, seiMessageList, onDump: () => {
            self.postMessage({
              type: 'dump',
              isAudio,
              data: tempFrame.data,
              userId: '',
              streamType: isMain ? RemoteStreamType.Main : RemoteStreamType.Aux
            });
          } });
        });
      }
      controller.enqueue(tempFrame);
    }
  });
}
function createReceiverTransform({ userId, streamType, isAudio }: { userId: string, streamType: string, isAudio: boolean}) {
  return new TransformStream({
    transform(frame, controller) {
      let tempFrame = frame;

      if (isAudio) {
        // @ts-ignore
        (audioDecodePipeline as AudioDecodeProcessor[]).forEach(processor => {
          tempFrame = processor({ frame: tempFrame, onAudioFrameNTPTime: ntpTime => {
            self.postMessage({
              type: 'audio-ntp',
              data: ntpTime,
              userId,
              streamType
            });
          }, onDump: () => {
            self.postMessage({
              type: 'dump',
              isAudio,
              data: tempFrame.data,
              userId
            });
          } });
        });
        controller.enqueue(tempFrame);
      } else {
        // @ts-ignore
        (videoDecodePipeline as VideoDecodeProcessor[]).forEach(processor => {
          tempFrame = processor({ frame: tempFrame, onSEI: naluList => {
            naluList.forEach(nalu => {
              self.postMessage({
                type: 'sei',
                seiPayloadType: nalu.seiPayloadType,
                data: nalu.seiPayload!.buffer,
                userId,
                streamType
              });
            });
          }, onDump: () => {
            self.postMessage({
              type: 'dump',
              isAudio,
              data: tempFrame.data,
              userId,
              streamType
            });
          }  });
        });
      }
      controller.enqueue(tempFrame);
    }
  });
}

interface CreateScriptTransformWorkerParam {
  audioEncodePipeline: (AudioEncodeProcessor|undefined)[],
  audioDecodePipeline: (AudioDecodeProcessor|undefined)[],
  videoEncodePipeline: (VideoEncodeProcessor|undefined)[],
  videoDecodePipeline: (VideoDecodeProcessor|undefined)[]
}

interface ScriptTransformWorkerMessage {
  type: 'sei'|'ntp-offset',
  isMain?: boolean,
  small?: boolean,
  data: any,
  seiPayloadType?: number,
  userId?: string,
  streamType?: RemoteStreamType
}

/**
 * 创建 worker，在 worker 通过 scriptTransform API（Firefox、Safari） 来处理做编解码前后处理，例如 SEI、加解密等。
 * 注意：所有复制到 worker 的 class/function/codecPipeline 必须是纯函数。
 */
export function createScriptTransformWorker(params: CreateScriptTransformWorkerParam) {
  // 复制到 worker 的 class
  const classList = [NALU];
  // 复制到 worker 的 function
  const funcList = [getNaluCount, isEmptyFrame, parseNALUs, hasSEI, encodeSEINalu, createSenderTransfrom, createReceiverTransform, getLast16Bits, concatArrayBuffers, getStartCodeLengthAt];
  // worker 的全局代码
  const globalCode = () => {
    const seiMessageList: SEI_MESSAGE[] = [];
    const seiSubMessageList: SEI_MESSAGE[] = [];
    const seiSmallMessageList: SEI_MESSAGE[] = [];
    let ntpOffset = 0;

    self.onmessage = (event: MessageEvent<ScriptTransformWorkerMessage>) => {
      switch (event.data.type) {
        case 'sei': {
          if (event.data.isMain) {
            seiMessageList.push(event.data.data);
            if (event.data.small) {
              seiSmallMessageList.push(event.data.data);
            }
          } else {
            seiSubMessageList.push(event.data.data);
          }
        }
          break;
        case 'ntp-offset':
          ntpOffset = event.data.data;
          break;
      }
    };
    self.onrtctransform = event => {
      // @ts-ignore
      const { options } = event.transformer;
      // @ts-ignore
      const transform = options.isReceiver ?
        createReceiverTransform({
          userId: options.userId,
          streamType: options.streamType,
          isAudio: options.isAudio
        }) :
        createSenderTransfrom({
          getNtpTime: () => Date.now() + ntpOffset,
          isAudio: options.isAudio,
          isMain: options.isMain,
          seiMessageList: options.isMain ? (options.small ? seiSmallMessageList : seiMessageList) : seiSubMessageList
        });
      // @ts-ignore
      event.transformer.readable.pipeThrough(transform).pipeTo(event.transformer.writable);
    };
  };
  const pipelines = `const videoEncodePipeline=[${params.videoEncodePipeline.toString()}];
                    const videoDecodePipeline=[${params.videoDecodePipeline.toString()}];
                    const audioEncodePipeline = [${params.audioEncodePipeline.toString()}];
                    const audioDecodePipeline = [${params.audioDecodePipeline.toString()}];`;
  const workerJSString = `(()=>{${classList.map(item => `const ${item.name}=(()=>${item.toString()})()`).join('\n')}\n${funcList.map(item => item.toString()).join('\n')};(${globalCode})();${pipelines}})()`;
  const blob = new Blob([workerJSString], { type: 'text/javascript' });
  const objURL = URL.createObjectURL(blob);
  const worker = new Worker(objURL);
  URL.revokeObjectURL(objURL);
  return worker;
}
